import { useState, type ReactNode } from 'react';
import { usePromoData } from '@hungryroot/promos';
import { Header, Heading, Paragraph, Text } from '@hungryroot/ramen';
import { type SignupResponse } from '@hungryroot/rest/api/signup/post';
import { ButtonApple, ButtonFacebook, ButtonGoogle } from '@hungryroot/sso';
import { AlreadyHaveAnAccount } from '../AlreadyHaveAnAccount/AlreadyHaveAnAccount';
import { Faq } from '../Faq/Faq';
import { SignUpForm } from '../SignUpForm/SignUpForm';

import styles from './SignUp.module.css';

type Props = {
  header: ReactNode;
  onNavigate: (route: string) => void;
  nonCheckedOutSignupSuccessUrl: string;
  onSignupSuccess: (data: {
    provider: 'email' | 'facebook' | 'apple' | 'google';
    data: SignupResponse;
  }) => void;
};

export function SignUp({
  header,
  onNavigate,
  onSignupSuccess,
  nonCheckedOutSignupSuccessUrl,
}: Props) {
  const isRedeemingGiftCard = nonCheckedOutSignupSuccessUrl.includes('redeem'); // ?next=/gift/redeem/:code

  function onSsoLoginSuccess(user: SignupResponse) {
    // ?next=/gift/redeem/:code as an example
    if (isRedeemingGiftCard) {
      window.location.href = nonCheckedOutSignupSuccessUrl;
    }

    const { did_checkout: userDidCheckout } = user;
    if (userDidCheckout === true) {
      onNavigate('/myhungryroot/');
      return;
    }

    onNavigate(nonCheckedOutSignupSuccessUrl);
  }

  const [ssoError, setSsoError] = useState(false);
  const { data: promoData } = usePromoData({
    page: '/signup/',
  });

  const title = isRedeemingGiftCard
    ? 'Sign up to redeem your gift card'
    : 'Claim your free gift';
  const description = isRedeemingGiftCard
    ? 'Create an account below to receive your feel good foods.'
    : promoData?.discount_effect
      ? `Create an account to claim your free gift for life and ${promoData?.discount_effect}.`
      : '';

  return (
    <div>
      {isRedeemingGiftCard ? <Header /> : header}
      <main className={styles.main}>
        <div className={styles.header}>
          <Heading className={styles.title} variant="H1Alt">
            {title}
          </Heading>
          {description ? (
            <Paragraph className={styles.promo}>{description}</Paragraph>
          ) : null}
        </div>
        <SignUpForm
          submitButtonText="Continue"
          heading={null}
          onSignupSuccess={(data) => {
            onSignupSuccess({
              data,
              provider: 'email',
            });
          }}
          ssoButtons={
            <>
              <div className={styles.sso}>
                <ButtonGoogle
                  onError={() => setSsoError(true)}
                  onSignupSuccess={(data) => {
                    onSignupSuccess({
                      data,
                      provider: 'google',
                    });
                  }}
                  onLoginSuccess={onSsoLoginSuccess}
                >
                  <Text weight="heavy" className={styles['sso-copy']}>
                    Sign in with Google
                  </Text>
                </ButtonGoogle>
                <ButtonApple
                  onError={() => setSsoError(true)}
                  onSignupSuccess={(data) => {
                    onSignupSuccess({
                      data,
                      provider: 'apple',
                    });
                  }}
                  onLoginSuccess={onSsoLoginSuccess}
                >
                  <Text weight="heavy" className={styles['sso-copy']}>
                    Sign in with Apple
                  </Text>
                </ButtonApple>
                <ButtonFacebook
                  onError={() => setSsoError(true)}
                  onSignupSuccess={(data) => {
                    onSignupSuccess({
                      data,
                      provider: 'facebook',
                    });
                  }}
                  onLoginSuccess={onSsoLoginSuccess}
                >
                  <Text weight="heavy" className={styles['sso-copy-facebook']}>
                    Continue with Facebook
                  </Text>
                </ButtonFacebook>
              </div>
              {ssoError && (
                <Paragraph className={styles['error-sso']} variant="caption">
                  The account cannot be accessed because the login credentials
                  could not be verified. Please try again or use a different
                  account to sign up.
                </Paragraph>
              )}
            </>
          }
        />
        <AlreadyHaveAnAccount
          nextRedirect={
            isRedeemingGiftCard ? nonCheckedOutSignupSuccessUrl : undefined
          }
        />
        <Faq />
      </main>
    </div>
  );
}
